import React from 'react';
import { FeatherCheckCircle, FeatherClock, FeatherAlertCircle, FeatherTarget, FeatherTrendingUp, FeatherUsers, FeatherBarChart, FeatherAlertTriangle, FeatherCheckSquare } from '../ui/icons';
import { Tooltip } from '../ui/Tooltip';
import './TodayOutcome.css';

const TodayOutcome = ({ outcomeData, onPersonalGoalToggle, isSavingGoal = false }) => {
    if (!outcomeData) {
        return (
            <div className="today-outcome-container">
                <div className="today-outcome-empty">No outcome data available</div>
            </div>
        );
    }

    const { taskMetrics, goals, featureProgress, resourceUtilization, timeSpent, taskHealth, managerGoals } = outcomeData;

    const getStatusColor = (status) => {
        switch (status) {
            case 'met':
                return 'green';
            case 'partial':
                return 'yellow';
            case 'below':
                return 'red';
            default:
                return 'gray';
        }
    };

    const getOverallStatus = () => {
        const statuses = [goals.featureCompletion.status, goals.taskCompletion.status, goals.pickupRate.status];
        if (statuses.every(s => s === 'met')) return { status: 'success', label: 'On Track', color: '#10b981' };
        if (statuses.some(s => s === 'below')) return { status: 'critical', label: 'Needs Attention', color: '#ef4444' };
        return { status: 'partial', label: 'Partial Success', color: '#f59e0b' };
    };

    const overallStatus = getOverallStatus();
    const goalsList = [
        { key: 'featureCompletion', label: 'Feature Completion', icon: <FeatherTarget size={20} />, ...goals.featureCompletion },
        { key: 'taskCompletion', label: 'Task Completion', icon: <FeatherCheckCircle size={20} />, ...goals.taskCompletion },
        { key: 'pickupRate', label: 'Pickup Rate', icon: <FeatherTrendingUp size={20} />, ...goals.pickupRate }
    ];

    const taskFlowItems = [
        { key: 'picked', label: 'Picked Up', value: taskMetrics.pickedUpToday, total: taskMetrics.totalAssigned, color: '#3b82f6', icon: <FeatherCheckCircle size={20} /> },
        { key: 'completed', label: 'Completed', value: taskMetrics.completedToday, total: taskMetrics.pickedUpToday, color: '#10b981', icon: <FeatherCheckCircle size={20} /> },
        { key: 'inProgress', label: 'In Progress', value: taskMetrics.inProgressToday, total: taskMetrics.pickedUpToday, color: '#f59e0b', icon: <FeatherClock size={20} /> }
    ];

    const featureItems = [
        { key: 'completed', label: 'Completed', value: featureProgress.completed, total: featureProgress.total, color: '#10b981', icon: <FeatherCheckCircle size={20} /> },
        { key: 'inProgress', label: 'In Progress', value: featureProgress.inProgress, total: featureProgress.total, color: '#f59e0b', icon: <FeatherClock size={20} /> },
        { key: 'total', label: 'Total', value: featureProgress.total, total: featureProgress.total, color: '#3b82f6', icon: <FeatherBarChart size={20} /> }
    ];

    const getKeyTakeaways = () => {
        const takeaways = [];
        if (goals.taskCompletion.status === 'met') {
            takeaways.push('Task completion goal achieved');
        } else if (goals.taskCompletion.status === 'below') {
            takeaways.push('Task completion below target - review workflow');
        }
        if (goals.pickupRate.status === 'met') {
            takeaways.push('All assigned tasks picked up');
        } else {
            takeaways.push('Some tasks remain unpicked - review assignments');
        }
        if (resourceUtilization.utilizationRate >= 80) {
            takeaways.push('Resource utilization optimal');
        } else if (resourceUtilization.utilizationRate < 50) {
            takeaways.push('Low resource utilization - optimize allocation');
        }
        if (taskMetrics.completionRate >= 80) {
            takeaways.push('High completion rate - excellent execution');
        }
        return takeaways.slice(0, 4);
    };

    const getTaskHealthItems = () => {
        const items = [];
        if (taskHealth) {
            if (taskHealth.tasksWithoutFeatures?.count > 0) {
                items.push({
                    label: 'Tasks without features',
                    value: taskHealth.tasksWithoutFeatures.count,
                    icon: <FeatherAlertTriangle size={16} />,
                    color: '#f59e0b',
                    tooltip: `${taskHealth.tasksWithoutFeatures.count} task${taskHealth.tasksWithoutFeatures.count !== 1 ? 's' : ''} ${taskHealth.tasksWithoutFeatures.count !== 1 ? 'are' : 'is'} not assigned to any feature. Consider organizing tasks into features for better tracking.`
                });
            }
            if (taskHealth.featuresWithoutDueDates?.featureCount > 0) {
                items.push({
                    label: 'Features without due dates',
                    value: `${taskHealth.featuresWithoutDueDates.featureCount} features, ${taskHealth.featuresWithoutDueDates.taskCount} tasks`,
                    icon: <FeatherAlertCircle size={16} />,
                    color: '#f59e0b',
                    tooltip: `${taskHealth.featuresWithoutDueDates.featureCount} feature${taskHealth.featuresWithoutDueDates.featureCount !== 1 ? 's' : ''} without due dates containing ${taskHealth.featuresWithoutDueDates.taskCount} task${taskHealth.featuresWithoutDueDates.taskCount !== 1 ? 's' : ''}.\nCompletion rate: ${taskHealth.featuresWithoutDueDates.completionRate}%\n\nConsider setting due dates for better prioritization.`
                });
            }
            if (taskHealth.staleTasks?.count > 0) {
                items.push({
                    label: 'Stale tasks',
                    value: taskHealth.staleTasks.count,
                    icon: <FeatherClock size={16} />,
                    color: '#ef4444',
                    tooltip: `${taskHealth.staleTasks.count} task${taskHealth.staleTasks.count !== 1 ? 's' : ''} created ${taskHealth.staleTasks.threshold}+ days ago that ${taskHealth.staleTasks.count !== 1 ? 'have' : 'has'} never been worked on.\n\nThese tasks may need reassignment or deprioritization.`
                });
            }
        }
        return items;
    };

    return (
        <div className="today-outcome-container">
            {/* Hero Section */}
            <div className="outcome-hero">
                <div className="outcome-hero-content">
                    <Tooltip
                        title="Overall Status"
                        description={`Based on three key goals:\n• Feature Completion: ${goals.featureCompletion.status}\n• Task Completion: ${goals.taskCompletion.status}\n• Pickup Rate: ${goals.pickupRate.status}\n\n${overallStatus.status === 'success' ? 'All goals are on track!' : overallStatus.status === 'critical' ? 'Some goals need immediate attention.' : 'Goals are partially achieved.'}`}
                        placement="bottom"
                    >
                        <div className="outcome-hero-status" style={{ '--status-color': overallStatus.color }}>
                            <div className="outcome-hero-status-icon">
                                {overallStatus.status === 'success' ? <FeatherCheckCircle size={48} /> : 
                                 overallStatus.status === 'critical' ? <FeatherAlertTriangle size={48} /> :
                                 <FeatherAlertCircle size={48} />}
                            </div>
                            <div className="outcome-hero-status-label">{overallStatus.label}</div>
                        </div>
                    </Tooltip>
                    <Tooltip
                        title="Completion Rate"
                        description={`${taskMetrics.completedToday} tasks completed out of ${taskMetrics.pickedUpToday} tasks picked up today.\n\nThis shows the efficiency of task execution after pickup.`}
                        placement="bottom"
                    >
                        <div className="outcome-hero-metric">
                            <div className="outcome-hero-metric-value">{taskMetrics.completionRate}%</div>
                            <div className="outcome-hero-metric-label">Completion Rate</div>
                        </div>
                    </Tooltip>
                </div>
            </div>

            {/* Executive Summary */}
            <div className="outcome-section outcome-executive-summary">
                <div className="executive-summary-card">
                    <div className="executive-summary-header">
                        <h3 className="executive-summary-title">Executive Summary</h3>
                        <div className="executive-summary-status" style={{ backgroundColor: `${overallStatus.color}15`, color: overallStatus.color }}>
                            {overallStatus.label}
                        </div>
                    </div>
                    <div className="executive-summary-takeaways">
                        {getKeyTakeaways().map((takeaway, index) => (
                            <div key={index} className="executive-summary-item">
                                <FeatherCheckCircle size={16} className="executive-summary-icon" />
                                <span>{takeaway}</span>
                            </div>
                        ))}
                    </div>
                    {getTaskHealthItems().length > 0 && (
                        <div className="executive-summary-task-health">
                            <div className="executive-summary-task-health-title">Task Health</div>
                            <div className="executive-summary-task-health-items">
                                {getTaskHealthItems().map((item, index) => (
                                    <Tooltip
                                        key={index}
                                        title={item.label}
                                        description={item.tooltip}
                                        placement="top"
                                    >
                                        <div className="executive-summary-task-health-item" style={{ color: item.color }}>
                                            <div className="executive-summary-task-health-icon" style={{ color: item.color }}>
                                                {item.icon}
                                            </div>
                                            <div className="executive-summary-task-health-content">
                                                <div className="executive-summary-task-health-label">{item.label}</div>
                                                <div className="executive-summary-task-health-value">{item.value}</div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Manager Goals Review */}
            {managerGoals && (
                <div className="outcome-section">
                    <h3 className="outcome-section-title">
                        <FeatherTarget size={20} />
                        Manager Goals Review
                    </h3>
                    <div className="manager-goals-review">
                        {(() => {
                            const allGoals = [
                                ...(managerGoals.tasks || []).map(g => ({ ...g, type: 'task' })),
                                ...(managerGoals.features || []).map(g => ({ ...g, type: 'feature' })),
                                ...(managerGoals.personal || []).map(g => ({ ...g, type: 'personal' }))
                            ];
                            const achievedCount = allGoals.filter(g => g.status === 'achieved').length;
                            const totalCount = allGoals.length;

                            return (
                                <>
                                    <div className="manager-goals-summary">
                                        <div className="manager-goals-summary-item">
                                            <div className="manager-goals-summary-value">{achievedCount}</div>
                                            <div className="manager-goals-summary-label">Achieved</div>
                                        </div>
                                        <div className="manager-goals-summary-divider"></div>
                                        <div className="manager-goals-summary-item">
                                            <div className="manager-goals-summary-value">{totalCount - achievedCount}</div>
                                            <div className="manager-goals-summary-label">Not Achieved</div>
                                        </div>
                                        <div className="manager-goals-summary-divider"></div>
                                        <div className="manager-goals-summary-item">
                                            <div className="manager-goals-summary-value">{totalCount}</div>
                                            <div className="manager-goals-summary-label">Total Goals</div>
                                        </div>
                                    </div>

                                    {managerGoals.tasks && managerGoals.tasks.length > 0 && (
                                        <div className="manager-goals-group">
                                            <h4 className="manager-goals-group-title">Task Goals</h4>
                                            <div className="manager-goals-list">
                                                {managerGoals.tasks.map((goal, index) => (
                                                    <div key={goal.taskId || index} className={`manager-goal-item manager-goal-${goal.status}`}>
                                                        <div className="manager-goal-status-icon">
                                                            {goal.status === 'achieved' ? (
                                                                <FeatherCheckCircle size={18} />
                                                            ) : goal.status === 'not-achieved' ? (
                                                                <FeatherAlertCircle size={18} />
                                                            ) : (
                                                                <FeatherClock size={18} />
                                                            )}
                                                        </div>
                                                        <div className="manager-goal-content">
                                                            <div className="manager-goal-name">[{goal.displayId}] {goal.taskTitle}</div>
                                                            <div className="manager-goal-target">Target: Complete Today</div>
                                                        </div>
                                                        <div className={`manager-goal-status-badge manager-goal-status-${goal.status}`}>
                                                            {goal.status === 'achieved' ? 'Achieved' : goal.status === 'not-achieved' ? 'Not Achieved' : 'Pending'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {managerGoals.features && managerGoals.features.length > 0 && (
                                        <div className="manager-goals-group">
                                            <h4 className="manager-goals-group-title">Feature Goals</h4>
                                            <div className="manager-goals-list">
                                                {managerGoals.features.map((goal, index) => (
                                                    <div key={goal.featureId || index} className={`manager-goal-item manager-goal-${goal.status}`}>
                                                        <div className="manager-goal-status-icon">
                                                            {goal.status === 'achieved' ? (
                                                                <FeatherCheckCircle size={18} />
                                                            ) : goal.status === 'not-achieved' ? (
                                                                <FeatherAlertCircle size={18} />
                                                            ) : (
                                                                <FeatherClock size={18} />
                                                            )}
                                                        </div>
                                                        <div className="manager-goal-content">
                                                            <div className="manager-goal-name">{goal.featureName}</div>
                                                            <div className="manager-goal-target">Target: Complete Today</div>
                                                        </div>
                                                        <div className={`manager-goal-status-badge manager-goal-status-${goal.status}`}>
                                                            {goal.status === 'achieved' ? 'Achieved' : goal.status === 'not-achieved' ? 'Not Achieved' : 'Pending'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {managerGoals.personal && managerGoals.personal.length > 0 && (
                                        <div className="manager-goals-group">
                                            <h4 className="manager-goals-group-title">Personal Goals</h4>
                                            <div className="manager-goals-list">
                                                {managerGoals.personal.map((goal, index) => (
                                                    <div key={goal.id || index} className={`manager-goal-item manager-goal-${goal.status}`}>
                                                        <div className="manager-goal-status-icon">
                                                            {goal.status === 'achieved' ? (
                                                                <FeatherCheckCircle size={18} />
                                                            ) : goal.status === 'not-achieved' ? (
                                                                <FeatherAlertCircle size={18} />
                                                            ) : (
                                                                <FeatherClock size={18} />
                                                            )}
                                                        </div>
                                                        <div className="manager-goal-content">
                                                            <div className="manager-goal-name">{goal.description}</div>
                                                            <div className="manager-goal-target">Target: Complete Today</div>
                                                        </div>
                                                        <div className="manager-goal-actions">
                                                            {goal.status !== 'not-achieved' && onPersonalGoalToggle && (
                                                                <button
                                                                    className={`manager-goal-toggle ${goal.status === 'achieved' ? 'achieved' : 'pending'} ${isSavingGoal ? 'saving' : ''}`}
                                                                    onClick={() => onPersonalGoalToggle(goal.id, goal.status)}
                                                                    disabled={isSavingGoal}
                                                                    title={goal.status === 'achieved' ? 'Mark as pending' : 'Mark as achieved'}
                                                                >
                                                                    <FeatherCheckSquare size={16} />
                                                                    <span>{goal.status === 'achieved' ? 'Mark Pending' : 'Mark Achieved'}</span>
                                                                </button>
                                                            )}
                                                            <div className={`manager-goal-status-badge manager-goal-status-${goal.status}`}>
                                                                {goal.status === 'achieved' ? 'Achieved' : goal.status === 'not-achieved' ? 'Not Achieved' : 'Pending'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {totalCount === 0 && (
                                        <div className="manager-goals-empty">
                                            No goals set for today.
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Goals Wave */}
            <div className="outcome-section">
                <h3 className="outcome-section-title">
                    <FeatherTarget size={20} />
                    Goals Achievement
                </h3>
                <div className="goals-wave">
                    <div className="goals-wave-container">
                        {goalsList.map((goal, index) => {
                            const statusColor = getStatusColor(goal.status);
                            const waveOffset = Math.sin((index / goalsList.length) * Math.PI * 2) * 30;
                            const delay = index * 0.15;
                            const percentage = Math.min((goal.achieved / goal.target) * 100, 100);

                            // Generate tooltip content based on goal type
                            let tooltipTitle = goal.label;
                            let tooltipDescription = '';
                            
                            if (goal.key === 'featureCompletion') {
                                const completedFeatures = featureProgress.features?.filter(f => f.completedTasks > 0) || [];
                                const featureNames = completedFeatures.map(f => f.name).slice(0, 5);
                                tooltipDescription = `Target: ${goal.target} features with completed tasks\nAchieved: ${goal.achieved} features\n\n${completedFeatures.length > 0 ? `Features with progress:\n${featureNames.join(', ')}${completedFeatures.length > 5 ? `\n...and ${completedFeatures.length - 5} more` : ''}` : 'No features have completed tasks yet.'}`;
                            } else if (goal.key === 'taskCompletion') {
                                const completedTasks = taskMetrics.completedTasks || [];
                                
                                // Group tasks by feature urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                const tasksByUrgency = {};
                                completedTasks.forEach(t => {
                                    const urgency = t.feature?.dueDateStatus || 'no-due-date';
                                    if (!tasksByUrgency[urgency]) tasksByUrgency[urgency] = [];
                                    tasksByUrgency[urgency].push(t);
                                });
                                
                                const sortedUrgencies = Object.keys(tasksByUrgency).sort((a, b) => urgencyOrder[a] - urgencyOrder[b]);
                                
                                let taskList = '';
                                sortedUrgencies.forEach(urgency => {
                                    const urgencyTasks = tasksByUrgency[urgency];
                                    const urgencyLabel = urgency === 'overdue' ? '⚠️ OVERDUE' : urgency === 'due-today' ? '⚠️ DUE TODAY' : urgency === 'urgent' ? '⚠️ URGENT' : urgency === 'upcoming' ? 'Upcoming' : urgency === 'normal' ? 'On Track' : 'No Due Date';
                                    const taskLines = urgencyTasks.slice(0, 3).map(t => {
                                        let featureInfo = '';
                                        if (t.feature) {
                                            if (t.feature.dueDateStatus === 'overdue') featureInfo = ` (${t.feature.name} - ${t.feature.daysOverdue}d overdue)`;
                                            else if (t.feature.dueDateStatus === 'due-today') featureInfo = ` (${t.feature.name} - due today)`;
                                            else if (t.feature.dueDateStatus === 'urgent') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else if (t.feature.dueDateStatus === 'upcoming') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else featureInfo = ` (${t.feature.name})`;
                                        }
                                        return `  [${t.display_id}] ${t.title}${featureInfo}`;
                                    }).join('\n');
                                    if (taskLines) {
                                        taskList += `${urgencyLabel}:\n${taskLines}\n`;
                                    }
                                });
                                
                                const more = completedTasks.length > 8 ? `\n...and ${completedTasks.length - 8} more` : '';
                                tooltipDescription = `Target: ${goal.target}% completion rate\nAchieved: ${goal.achieved}%\n\nBreakdown:\n• Completed: ${taskMetrics.completedToday} tasks\n• In Progress: ${taskMetrics.inProgressToday} tasks\n• Picked Up: ${taskMetrics.pickedUpToday} tasks\n\n${goal.status === 'met' ? 'Goal achieved! Excellent execution.' : goal.status === 'partial' ? 'Close to target. Review workflow for improvements.' : 'Below target. Consider reviewing task complexity or resource allocation.'}\n\n${completedTasks.length > 0 ? `Completed tasks (grouped by feature urgency):\n${taskList}${more}` : 'No tasks completed yet.'}`;
                            } else if (goal.key === 'pickupRate') {
                                const unpicked = taskMetrics.totalAssigned - taskMetrics.pickedUpToday;
                                const unpickedTasks = taskMetrics.unpickedTasks || [];
                                
                                // Group tasks by feature urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                const tasksByUrgency = {};
                                unpickedTasks.forEach(t => {
                                    const urgency = t.feature?.dueDateStatus || 'no-due-date';
                                    if (!tasksByUrgency[urgency]) tasksByUrgency[urgency] = [];
                                    tasksByUrgency[urgency].push(t);
                                });
                                
                                const sortedUrgencies = Object.keys(tasksByUrgency).sort((a, b) => urgencyOrder[a] - urgencyOrder[b]);
                                
                                let taskList = '';
                                sortedUrgencies.forEach(urgency => {
                                    const urgencyTasks = tasksByUrgency[urgency];
                                    const urgencyLabel = urgency === 'overdue' ? '⚠️ OVERDUE' : urgency === 'due-today' ? '⚠️ DUE TODAY' : urgency === 'urgent' ? '⚠️ URGENT' : urgency === 'upcoming' ? 'Upcoming' : urgency === 'normal' ? 'On Track' : 'No Due Date';
                                    const taskLines = urgencyTasks.slice(0, 3).map(t => {
                                        let featureInfo = '';
                                        if (t.feature) {
                                            if (t.feature.dueDateStatus === 'overdue') featureInfo = ` (${t.feature.name} - ${t.feature.daysOverdue}d overdue)`;
                                            else if (t.feature.dueDateStatus === 'due-today') featureInfo = ` (${t.feature.name} - due today)`;
                                            else if (t.feature.dueDateStatus === 'urgent') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else if (t.feature.dueDateStatus === 'upcoming') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else featureInfo = ` (${t.feature.name})`;
                                        }
                                        return `  [${t.display_id}] ${t.title}${featureInfo}`;
                                    }).join('\n');
                                    if (taskLines) {
                                        taskList += `${urgencyLabel}:\n${taskLines}\n`;
                                    }
                                });
                                
                                const more = unpickedTasks.length > 8 ? `\n...and ${unpickedTasks.length - 8} more` : '';
                                tooltipDescription = `Target: ${goal.target}% pickup rate\nAchieved: ${goal.achieved}%\n\nBreakdown:\n• Picked Up: ${taskMetrics.pickedUpToday} tasks\n• Total Assigned: ${taskMetrics.totalAssigned} tasks\n• Unpicked: ${unpicked} tasks\n\n${goal.status === 'met' ? 'All assigned tasks were picked up!' : unpicked > 0 ? `${unpicked} task${unpicked > 1 ? 's' : ''} remain${unpicked > 1 ? '' : 's'} unpicked. Review assignments.` : 'No tasks assigned.'}\n\n${unpickedTasks.length > 0 ? `Unpicked tasks (grouped by feature urgency):\n${taskList}${more}` : unpicked === 0 ? 'All tasks picked up!' : 'No unpicked tasks to display.'}`;
                            }

                            return (
                                <Tooltip
                                    key={goal.key}
                                    title={tooltipTitle}
                                    description={tooltipDescription}
                                    placement="top"
                                >
                                    <div
                                        className="goals-wave-item"
                                        style={{
                                            '--wave-offset': `${waveOffset}px`,
                                            '--delay': `${delay}s`,
                                            '--color': statusColor === 'green' ? '#10b981' : statusColor === 'yellow' ? '#f59e0b' : '#ef4444'
                                        }}
                                    >
                                        <div className="goals-wave-icon" style={{ 
                                            color: statusColor === 'green' ? '#10b981' : statusColor === 'yellow' ? '#f59e0b' : '#ef4444',
                                            backgroundColor: statusColor === 'green' ? '#10b98115' : statusColor === 'yellow' ? '#f59e0b15' : '#ef444415'
                                        }}>
                                            {goal.icon}
                                        </div>
                                        <div className="goals-wave-label">{goal.label}</div>
                                        <div className="goals-wave-stats">
                                            <span className="goals-wave-achieved">{goal.achieved}{goal.key === 'taskCompletion' || goal.key === 'pickupRate' ? '%' : ''}</span>
                                            <span className="goals-wave-separator">/</span>
                                            <span className="goals-wave-target">{goal.target}{goal.key === 'taskCompletion' || goal.key === 'pickupRate' ? '%' : ''}</span>
                                        </div>
                                        <div className="goals-wave-progress">
                                            <div 
                                                className="goals-wave-progress-fill"
                                                style={{ 
                                                    width: `${percentage}%`,
                                                    backgroundColor: statusColor === 'green' ? '#10b981' : statusColor === 'yellow' ? '#f59e0b' : '#ef4444'
                                                }}
                                            />
                                        </div>
                                        <div className={`goals-wave-status goals-wave-status-${statusColor}`}>
                                            {goal.status.toUpperCase()}
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                    <div className="goals-wave-summary">
                        <div className="goals-wave-summary-icon">
                            <FeatherTarget size={24} />
                        </div>
                        <div className="goals-wave-summary-text">
                            <div className="goals-wave-summary-count">
                                {goalsList.filter(g => g.status === 'met').length} / {goalsList.length}
                            </div>
                            <div className="goals-wave-summary-label">Goals Achieved</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Task Flow Wave */}
            <div className="outcome-section">
                <h3 className="outcome-section-title">
                    <FeatherBarChart size={20} />
                    Task Flow
                </h3>
                <div className="task-flow-wave">
                    <div className="task-flow-wave-container">
                        {taskFlowItems.map((item, index) => {
                            const waveOffset = Math.sin((index / taskFlowItems.length) * Math.PI * 2) * 40;
                            const delay = index * 0.2;
                            const percentage = item.total > 0 ? (item.value / item.total) * 100 : 0;

                            // Generate tooltip content
                            let tooltipTitle = item.label;
                            let tooltipDescription = '';
                            
                            if (item.key === 'picked') {
                                const unpicked = item.total - item.value;
                                const tasks = taskMetrics.pickedUpTasks || [];
                                
                                // Group tasks by feature urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                const tasksByUrgency = {};
                                tasks.forEach(t => {
                                    const urgency = t.feature?.dueDateStatus || 'no-due-date';
                                    if (!tasksByUrgency[urgency]) tasksByUrgency[urgency] = [];
                                    tasksByUrgency[urgency].push(t);
                                });
                                
                                // Sort urgency groups and tasks within groups
                                const sortedUrgencies = Object.keys(tasksByUrgency).sort((a, b) => urgencyOrder[a] - urgencyOrder[b]);
                                
                                let taskList = '';
                                sortedUrgencies.forEach(urgency => {
                                    const urgencyTasks = tasksByUrgency[urgency];
                                    const urgencyLabel = urgency === 'overdue' ? '⚠️ OVERDUE' : urgency === 'due-today' ? '⚠️ DUE TODAY' : urgency === 'urgent' ? '⚠️ URGENT' : urgency === 'upcoming' ? 'Upcoming' : urgency === 'normal' ? 'On Track' : 'No Due Date';
                                    const taskLines = urgencyTasks.slice(0, 3).map(t => {
                                        let featureInfo = '';
                                        if (t.feature) {
                                            if (t.feature.dueDateStatus === 'overdue') featureInfo = ` (${t.feature.name} - ${t.feature.daysOverdue}d overdue)`;
                                            else if (t.feature.dueDateStatus === 'due-today') featureInfo = ` (${t.feature.name} - due today)`;
                                            else if (t.feature.dueDateStatus === 'urgent') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else if (t.feature.dueDateStatus === 'upcoming') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else featureInfo = ` (${t.feature.name})`;
                                        }
                                        return `  [${t.display_id}] ${t.title}${featureInfo}`;
                                    }).join('\n');
                                    if (taskLines) {
                                        taskList += `${urgencyLabel}:\n${taskLines}\n`;
                                    }
                                });
                                
                                const more = tasks.length > 8 ? `\n...and ${tasks.length - 8} more` : '';
                                tooltipDescription = `${item.value} tasks picked up out of ${item.total} assigned.\n\n${unpicked > 0 ? `${unpicked} task${unpicked > 1 ? 's' : ''} remain${unpicked > 1 ? '' : 's'} unpicked.` : 'All assigned tasks were picked up!'}\n\nPickup rate: ${taskMetrics.pickupRate}%\n\n${tasks.length > 0 ? `Picked tasks (grouped by feature urgency):\n${taskList}${more}` : 'No tasks picked up yet.'}`;
                            } else if (item.key === 'completed') {
                                const completionRate = item.total > 0 ? Math.round((item.value / item.total) * 100) : 0;
                                const tasks = taskMetrics.completedTasks || [];
                                
                                // Group tasks by feature urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                const tasksByUrgency = {};
                                tasks.forEach(t => {
                                    const urgency = t.feature?.dueDateStatus || 'no-due-date';
                                    if (!tasksByUrgency[urgency]) tasksByUrgency[urgency] = [];
                                    tasksByUrgency[urgency].push(t);
                                });
                                
                                const sortedUrgencies = Object.keys(tasksByUrgency).sort((a, b) => urgencyOrder[a] - urgencyOrder[b]);
                                
                                let taskList = '';
                                sortedUrgencies.forEach(urgency => {
                                    const urgencyTasks = tasksByUrgency[urgency];
                                    const urgencyLabel = urgency === 'overdue' ? '⚠️ OVERDUE' : urgency === 'due-today' ? '⚠️ DUE TODAY' : urgency === 'urgent' ? '⚠️ URGENT' : urgency === 'upcoming' ? 'Upcoming' : urgency === 'normal' ? 'On Track' : 'No Due Date';
                                    const taskLines = urgencyTasks.slice(0, 3).map(t => {
                                        let featureInfo = '';
                                        if (t.feature) {
                                            if (t.feature.dueDateStatus === 'overdue') featureInfo = ` (${t.feature.name} - ${t.feature.daysOverdue}d overdue)`;
                                            else if (t.feature.dueDateStatus === 'due-today') featureInfo = ` (${t.feature.name} - due today)`;
                                            else if (t.feature.dueDateStatus === 'urgent') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else if (t.feature.dueDateStatus === 'upcoming') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else featureInfo = ` (${t.feature.name})`;
                                        }
                                        return `  [${t.display_id}] ${t.title}${featureInfo}`;
                                    }).join('\n');
                                    if (taskLines) {
                                        taskList += `${urgencyLabel}:\n${taskLines}\n`;
                                    }
                                });
                                
                                const more = tasks.length > 8 ? `\n...and ${tasks.length - 8} more` : '';
                                tooltipDescription = `${item.value} tasks completed out of ${item.total} tasks picked up.\n\nCompletion rate: ${completionRate}%\n\n${item.value === item.total ? 'All picked tasks completed!' : `${item.total - item.value} task${item.total - item.value > 1 ? 's' : ''} still in progress.`}\n\n${tasks.length > 0 ? `Completed tasks (grouped by feature urgency):\n${taskList}${more}` : 'No tasks completed yet.'}`;
                            } else if (item.key === 'inProgress') {
                                const tasks = taskMetrics.inProgressTasks || [];
                                
                                // Group tasks by feature urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                const tasksByUrgency = {};
                                tasks.forEach(t => {
                                    const urgency = t.feature?.dueDateStatus || 'no-due-date';
                                    if (!tasksByUrgency[urgency]) tasksByUrgency[urgency] = [];
                                    tasksByUrgency[urgency].push(t);
                                });
                                
                                const sortedUrgencies = Object.keys(tasksByUrgency).sort((a, b) => urgencyOrder[a] - urgencyOrder[b]);
                                
                                let taskList = '';
                                sortedUrgencies.forEach(urgency => {
                                    const urgencyTasks = tasksByUrgency[urgency];
                                    const urgencyLabel = urgency === 'overdue' ? '⚠️ OVERDUE' : urgency === 'due-today' ? '⚠️ DUE TODAY' : urgency === 'urgent' ? '⚠️ URGENT' : urgency === 'upcoming' ? 'Upcoming' : urgency === 'normal' ? 'On Track' : 'No Due Date';
                                    const taskLines = urgencyTasks.slice(0, 3).map(t => {
                                        let featureInfo = '';
                                        if (t.feature) {
                                            if (t.feature.dueDateStatus === 'overdue') featureInfo = ` (${t.feature.name} - ${t.feature.daysOverdue}d overdue)`;
                                            else if (t.feature.dueDateStatus === 'due-today') featureInfo = ` (${t.feature.name} - due today)`;
                                            else if (t.feature.dueDateStatus === 'urgent') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else if (t.feature.dueDateStatus === 'upcoming') featureInfo = ` (${t.feature.name} - ${t.feature.daysUntilDue}d)`;
                                            else featureInfo = ` (${t.feature.name})`;
                                        }
                                        return `  [${t.display_id}] ${t.title}${featureInfo}`;
                                    }).join('\n');
                                    if (taskLines) {
                                        taskList += `${urgencyLabel}:\n${taskLines}\n`;
                                    }
                                });
                                
                                const more = tasks.length > 8 ? `\n...and ${tasks.length - 8} more` : '';
                                tooltipDescription = `${item.value} tasks still in progress out of ${item.total} tasks picked up.\n\nThese tasks were picked up today but not yet completed. They may carry over to the next day.\n\n${tasks.length > 0 ? `In-progress tasks (grouped by feature urgency):\n${taskList}${more}` : 'No tasks in progress.'}`;
                            }

                            return (
                                <Tooltip
                                    key={item.key}
                                    title={tooltipTitle}
                                    description={tooltipDescription}
                                    placement="top"
                                >
                                    <div
                                        className="task-flow-wave-item"
                                        style={{
                                            '--wave-offset': `${waveOffset}px`,
                                            '--delay': `${delay}s`,
                                            '--color': item.color
                                        }}
                                    >
                                        <div className="task-flow-wave-icon" style={{ 
                                            color: item.color,
                                            backgroundColor: `${item.color}15`
                                        }}>
                                            {item.icon}
                                        </div>
                                        <div className="task-flow-wave-label">{item.label}</div>
                                        <div className="task-flow-wave-value">{item.value}</div>
                                        <div className="task-flow-wave-sublabel">of {item.total}</div>
                                        <div className="task-flow-wave-progress">
                                            <div 
                                                className="task-flow-wave-progress-fill"
                                                style={{ 
                                                    width: `${Math.min(percentage, 100)}%`,
                                                    backgroundColor: item.color
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Feature Prioritization */}
            {featureProgress.urgencySummary && (
                <div className="outcome-section">
                    <h3 className="outcome-section-title">
                        <FeatherAlertTriangle size={20} />
                        Feature Prioritization by Due Date
                    </h3>
                    <div className="feature-prioritization-wave">
                        <div className="feature-prioritization-container">
                            {[
                                { key: 'overdue', label: 'Overdue', count: featureProgress.urgencySummary.overdue, color: '#ef4444', icon: <FeatherAlertTriangle size={20} /> },
                                { key: 'dueToday', label: 'Due Today', count: featureProgress.urgencySummary.dueToday, color: '#f59e0b', icon: <FeatherClock size={20} /> },
                                { key: 'urgent', label: 'Due Soon', count: featureProgress.urgencySummary.urgent, color: '#f59e0b', icon: <FeatherClock size={20} /> },
                                { key: 'upcoming', label: 'Upcoming', count: featureProgress.urgencySummary.upcoming, color: '#3b82f6', icon: <FeatherTarget size={20} /> },
                                { key: 'normal', label: 'On Track', count: featureProgress.urgencySummary.normal, color: '#10b981', icon: <FeatherCheckCircle size={20} /> }
                            ].filter(item => item.count > 0).map((item, index, filteredItems) => {
                                const waveOffset = Math.sin((index / filteredItems.length) * Math.PI * 2) * 40;
                                const delay = index * 0.2;
                                
                                const featuresInCategory = featureProgress.features?.filter(f => {
                                    if (item.key === 'overdue') return f.dueDateStatus === 'overdue';
                                    if (item.key === 'dueToday') return f.dueDateStatus === 'due-today';
                                    if (item.key === 'urgent') return f.dueDateStatus === 'urgent';
                                    if (item.key === 'upcoming') return f.dueDateStatus === 'upcoming';
                                    if (item.key === 'normal') return f.dueDateStatus === 'normal';
                                    return false;
                                }) || [];

                                const featureNames = featuresInCategory.map(f => {
                                    let statusText = '';
                                    if (f.dueDateStatus === 'overdue') statusText = ` (${f.daysOverdue} days overdue)`;
                                    else if (f.dueDateStatus === 'due-today') statusText = ' (due today)';
                                    else if (f.dueDateStatus === 'urgent') statusText = ` (${f.daysUntilDue} days)`;
                                    else if (f.dueDateStatus === 'upcoming') statusText = ` (${f.daysUntilDue} days)`;
                                    return `${f.name}${statusText}`;
                                }).slice(0, 8).join('\n');
                                const more = featuresInCategory.length > 8 ? `\n...and ${featuresInCategory.length - 8} more` : '';

                                return (
                                    <Tooltip
                                        key={item.key}
                                        title={item.label}
                                        description={`${item.count} feature${item.count !== 1 ? 's' : ''} ${item.label.toLowerCase()}\n\n${featuresInCategory.length > 0 ? `Features:\n${featureNames}${more}` : 'No features in this category.'}`}
                                        placement="top"
                                    >
                                        <div
                                            className={`feature-prioritization-item feature-prioritization-${item.key}`}
                                            style={{
                                                '--wave-offset': `${waveOffset}px`,
                                                '--delay': `${delay}s`,
                                                '--color': item.color
                                            }}
                                        >
                                            <div className="feature-prioritization-icon" style={{ 
                                                color: item.color,
                                                backgroundColor: `${item.color}15`
                                            }}>
                                                {item.icon}
                                            </div>
                                            <div className="feature-prioritization-label">{item.label}</div>
                                            <div className="feature-prioritization-value">{item.count}</div>
                                            {item.key === 'overdue' && item.count > 0 && (
                                                <div className="feature-prioritization-badge feature-prioritization-badge-overdue">
                                                    URGENT
                                                </div>
                                            )}
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Feature Progress Wave */}
            <div className="outcome-section">
                <h3 className="outcome-section-title">
                    <FeatherTarget size={20} />
                    Feature Progress
                </h3>
                <div className="feature-wave">
                    <div className="feature-wave-container">
                        {featureItems.map((item, index) => {
                            const waveOffset = Math.sin((index / featureItems.length) * Math.PI * 2) * 35;
                            const delay = index * 0.18;
                            const percentage = item.total > 0 ? (item.value / item.total) * 100 : 0;

                            // Generate tooltip content
                            let tooltipTitle = item.label;
                            let tooltipDescription = '';
                            
                            if (item.key === 'completed') {
                                const completedFeatures = featureProgress.features?.filter(f => f.totalTasks > 0 && f.completedTasks === f.totalTasks) || [];
                                const featureNames = completedFeatures.map(f => {
                                    let statusText = '';
                                    if (f.dueDateStatus === 'overdue') statusText = ` ⚠️ (${f.daysOverdue} days overdue)`;
                                    else if (f.dueDateStatus === 'due-today') statusText = ' ⚠️ (due today)';
                                    else if (f.dueDateStatus === 'urgent') statusText = ` ⚠️ (${f.daysUntilDue} days)`;
                                    return `${f.name}${statusText}`;
                                }).slice(0, 5);
                                tooltipDescription = `${item.value} feature${item.value !== 1 ? 's' : ''} fully completed.\n\n${completedFeatures.length > 0 ? `Completed features:\n${featureNames.join('\n')}${completedFeatures.length > 5 ? `\n...and ${completedFeatures.length - 5} more` : ''}` : 'No features fully completed yet.'}`;
                            } else if (item.key === 'inProgress') {
                                const inProgressFeatures = featureProgress.features?.filter(f => (f.inProgressTasks > 0 || f.completedTasks > 0 && f.completedTasks < f.totalTasks)) || [];
                                // Sort by urgency
                                const urgencyOrder = { 'overdue': 0, 'due-today': 1, 'urgent': 2, 'upcoming': 3, 'normal': 4, 'no-due-date': 5 };
                                inProgressFeatures.sort((a, b) => urgencyOrder[a.dueDateStatus] - urgencyOrder[b.dueDateStatus]);
                                const featureNames = inProgressFeatures.map(f => {
                                    let statusText = '';
                                    if (f.dueDateStatus === 'overdue') statusText = ` ⚠️ (${f.daysOverdue} days overdue)`;
                                    else if (f.dueDateStatus === 'due-today') statusText = ' ⚠️ (due today)';
                                    else if (f.dueDateStatus === 'urgent') statusText = ` ⚠️ (${f.daysUntilDue} days)`;
                                    else if (f.dueDateStatus === 'upcoming') statusText = ` (${f.daysUntilDue} days)`;
                                    return `${f.name}${statusText}`;
                                }).slice(0, 5);
                                tooltipDescription = `${item.value} feature${item.value !== 1 ? 's' : ''} with tasks in progress.\n\n${inProgressFeatures.length > 0 ? `Features in progress:\n${featureNames.join('\n')}${inProgressFeatures.length > 5 ? `\n...and ${inProgressFeatures.length - 5} more` : ''}` : 'No features in progress.'}`;
                            } else if (item.key === 'total') {
                                const allFeatures = featureProgress.features || [];
                                const featureNames = allFeatures.map(f => {
                                    let statusText = '';
                                    if (f.dueDateStatus === 'overdue') statusText = ` ⚠️ (${f.daysOverdue} days overdue)`;
                                    else if (f.dueDateStatus === 'due-today') statusText = ' ⚠️ (due today)';
                                    else if (f.dueDateStatus === 'urgent') statusText = ` ⚠️ (${f.daysUntilDue} days)`;
                                    else if (f.dueDateStatus === 'upcoming') statusText = ` (${f.daysUntilDue} days)`;
                                    return `${f.name}${statusText}`;
                                }).slice(0, 5);
                                tooltipDescription = `Total: ${item.value} feature${item.value !== 1 ? 's' : ''} with tasks\nCompletion rate: ${featureProgress.completionRate}%\n\n${allFeatures.length > 0 ? `Features:\n${featureNames.join('\n')}${allFeatures.length > 5 ? `\n...and ${allFeatures.length - 5} more` : ''}` : 'No features found.'}`;
                            }

                            return (
                                <Tooltip
                                    key={item.key}
                                    title={tooltipTitle}
                                    description={tooltipDescription}
                                    placement="top"
                                >
                                    <div
                                        className="feature-wave-item"
                                        style={{
                                            '--wave-offset': `${waveOffset}px`,
                                            '--delay': `${delay}s`,
                                            '--color': item.color
                                        }}
                                    >
                                        <div className="feature-wave-icon" style={{ 
                                            color: item.color,
                                            backgroundColor: `${item.color}15`
                                        }}>
                                            {item.icon}
                                        </div>
                                        <div className="feature-wave-label">{item.label}</div>
                                        <div className="feature-wave-value">{item.value}</div>
                                        {item.key === 'total' && (
                                            <div className="feature-wave-sublabel">{featureProgress.completionRate}% completion</div>
                                        )}
                                        <div className="feature-wave-progress">
                                            <div 
                                                className="feature-wave-progress-fill"
                                                style={{ 
                                                    width: `${Math.min(percentage, 100)}%`,
                                                    backgroundColor: item.color
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Resource Utilization */}
            <div className="outcome-section">
                <h3 className="outcome-section-title">
                    <FeatherUsers size={20} />
                    Resource Utilization
                </h3>
                <div className="resource-utilization-wave">
                    <div className="resource-utilization-hero">
                        <Tooltip
                            title="Total Hours Clocked"
                            description={(() => {
                                const resourceDetails = resourceUtilization.resourceDetails || [];
                                if (resourceDetails.length === 0) return 'No hours logged yet.';
                                const breakdown = resourceDetails.slice(0, 5).map(r => `${r.name}: ${r.hours}h`).join('\n');
                                const more = resourceDetails.length > 5 ? `\n...and ${resourceDetails.length - 5} more` : '';
                                return `Total: ${resourceUtilization.hoursSpent} hours\n\nBreakdown:\n${breakdown}${more}`;
                            })()}
                            placement="bottom"
                        >
                            <div className="resource-utilization-hero-metric">
                                <div className="resource-utilization-hero-value">{resourceUtilization.hoursSpent}</div>
                                <div className="resource-utilization-hero-label">Total Hours Clocked</div>
                            </div>
                        </Tooltip>
                        <div className="resource-utilization-hero-divider"></div>
                        <Tooltip
                            title="Resources Participating"
                            description={(() => {
                                const resourceDetails = resourceUtilization.resourceDetails || [];
                                if (resourceDetails.length === 0) return 'No resources participating.';
                                const names = resourceDetails.map(r => r.name).slice(0, 5).join(', ');
                                const more = resourceDetails.length > 5 ? `, and ${resourceDetails.length - 5} more` : '';
                                return `Total: ${resourceUtilization.resourcesCount} resource${resourceUtilization.resourcesCount !== 1 ? 's' : ''}\n\nResources:\n${names}${more}`;
                            })()}
                            placement="bottom"
                        >
                            <div className="resource-utilization-hero-metric">
                                <div className="resource-utilization-hero-value">{resourceUtilization.resourcesCount}</div>
                                <div className="resource-utilization-hero-label">Resources Participating</div>
                            </div>
                        </Tooltip>
                    </div>
                    <div className="resource-utilization-content">
                        <Tooltip
                            title="Average Hours per Resource"
                            description={(() => {
                                const resourceDetails = resourceUtilization.resourceDetails || [];
                                if (resourceDetails.length === 0) return 'No data available.';
                                const hours = resourceDetails.map(r => r.hours).sort((a, b) => a - b);
                                const min = hours[0] || 0;
                                const max = hours[hours.length - 1] || 0;
                                return `Average: ${resourceUtilization.averageHoursPerResource} hours\nRange: ${min}h - ${max}h\n\n${resourceUtilization.averageHoursPerResource < 4 ? 'Low average suggests resources may be spread across multiple projects.' : resourceUtilization.averageHoursPerResource >= 6 ? 'Good focus - resources dedicated to this project.' : 'Moderate engagement.'}`;
                            })()}
                            placement="top"
                        >
                            <div className="resource-utilization-item">
                                <div className="resource-utilization-label">Average Hours per Resource</div>
                                <div className="resource-utilization-value">{resourceUtilization.averageHoursPerResource}</div>
                            </div>
                        </Tooltip>
                        <Tooltip
                            title="Resources on Other Projects"
                            description={(() => {
                                const otherProjectsNames = resourceUtilization.resourcesOnOtherProjectsNames || [];
                                if (otherProjectsNames.length === 0) {
                                    return 'All resources are focused solely on this project.';
                                }
                                const names = otherProjectsNames.slice(0, 5).join(', ');
                                const more = otherProjectsNames.length > 5 ? `\n...and ${otherProjectsNames.length - 5} more` : '';
                                return `${resourceUtilization.resourcesOnOtherProjects} of ${resourceUtilization.resourcesCount} resources are also working on other projects today.\n\nResources on multiple projects:\n${names}${more}\n\n${resourceUtilization.resourcesOnOtherProjectsPercentage > 50 ? 'Most resources are multi-tasking. Consider reviewing allocation.' : 'Some resources are multi-tasking.'}`;
                            })()}
                            placement="top"
                        >
                            <div className="resource-utilization-item">
                                <div className="resource-utilization-label">Resources on Other Projects</div>
                                <div className="resource-utilization-value resource-utilization-multi-project">
                                    {resourceUtilization.resourcesOnOtherProjects} of {resourceUtilization.resourcesCount}
                                    {resourceUtilization.resourcesOnOtherProjectsPercentage > 0 && (
                                        <span className="resource-utilization-percentage">
                                            ({resourceUtilization.resourcesOnOtherProjectsPercentage}%)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                    {resourceUtilization.insights && resourceUtilization.insights.length > 0 && (
                        <div className="resource-utilization-insights">
                            <div className="resource-utilization-insights-header">
                                <FeatherAlertCircle size={18} />
                                <span>Insights</span>
                            </div>
                            <div className="resource-utilization-insights-list">
                                {resourceUtilization.insights.map((insight, index) => (
                                    <div key={index} className="resource-utilization-insight-item">
                                        <FeatherCheckCircle size={14} />
                                        <span>{insight}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Time Spent Analysis */}
            <div className="outcome-section">
                <h3 className="outcome-section-title">
                    <FeatherClock size={20} />
                    Time Analysis
                </h3>
                <div className="time-analysis-wave">
                    <div className="time-analysis-container">
                        <Tooltip
                            title="Total Hours"
                            description={(() => {
                                const resourceDetails = resourceUtilization.resourceDetails || [];
                                if (resourceDetails.length === 0) return 'No hours logged.';
                                const breakdown = resourceDetails.slice(0, 5).map(r => `${r.name}: ${r.hours}h`).join('\n');
                                const more = resourceDetails.length > 5 ? `\n...and ${resourceDetails.length - 5} more` : '';
                                return `Total: ${timeSpent.totalHours} hours logged today\n\nBreakdown by resource:\n${breakdown}${more}`;
                            })()}
                            placement="top"
                        >
                            <div className="time-analysis-item">
                                <div className="time-analysis-icon" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
                                    <FeatherClock size={24} />
                                </div>
                                <div className="time-analysis-content">
                                    <div className="time-analysis-value">{timeSpent.totalHours}</div>
                                    <div className="time-analysis-label">Total Hours</div>
                                </div>
                            </div>
                        </Tooltip>
                        <Tooltip
                            title="Average Hours per Task"
                            description={`Average: ${timeSpent.averagePerTask} hours per task\n\nBased on ${taskMetrics.pickedUpToday} tasks picked up and ${timeSpent.totalHours} total hours.\n\n${timeSpent.averagePerTask < 2 ? 'Tasks are being completed quickly.' : timeSpent.averagePerTask > 4 ? 'Tasks are taking longer than expected. Review complexity.' : 'Average task duration is reasonable.'}`}
                            placement="top"
                        >
                            <div className="time-analysis-item">
                                <div className="time-analysis-icon" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
                                    <FeatherBarChart size={24} />
                                </div>
                                <div className="time-analysis-content">
                                    <div className="time-analysis-value">{timeSpent.averagePerTask}</div>
                                    <div className="time-analysis-label">Avg per Task</div>
                                </div>
                            </div>
                        </Tooltip>
                        <Tooltip
                            title="Tasks per Hour (Efficiency)"
                            description={`Efficiency: ${timeSpent.efficiency} tasks completed per hour\n\nThis metric shows how many tasks are completed for every hour spent.\n\nCalculation: ${taskMetrics.completedToday} completed tasks ÷ ${timeSpent.totalHours} hours = ${timeSpent.efficiency}\n\n${timeSpent.efficiency > 0.5 ? 'High efficiency - good task completion rate.' : timeSpent.efficiency > 0.2 ? 'Moderate efficiency.' : 'Low efficiency - review task execution process.'}`}
                            placement="top"
                        >
                            <div className="time-analysis-item">
                                <div className="time-analysis-icon" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
                                    <FeatherTrendingUp size={24} />
                                </div>
                                <div className="time-analysis-content">
                                    <div className="time-analysis-value">{timeSpent.efficiency}</div>
                                    <div className="time-analysis-label">Tasks/Hour</div>
                                </div>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TodayOutcome;
